import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@material-ui/core/Checkbox";
import MatTableHead from "./MatTableHead";
import { Link } from "react-router-dom";
import { Tooltip } from "@material-ui/core";
import moment from "moment";

import { buildingSearchColumns } from "./columnConfig";

import {
	_capitalizeText,
	_formatNumber,
	_formatPrice,
	_shouldShow,
	_getBuildingURL,
	_formatNameAddress,
} from "../../../shared/helpers/utils";

const formatArrayField = (val) => {
	if (Array.isArray(val)) return val.filter(Boolean).join(", ");
	return val || "--";
};

const cellRenderers = {
	display_full_street_address: (d) => {
		const buildingNameOrAdr =
			d.name && _shouldShow(d.name)
				? d.name
				: d.display_full_street_address;
		const adr = d.display_full_street_address || "";
		const hood = Array.isArray(d.neighborhood)
			? d.neighborhood[0]
			: d.neighborhood;
		const boro = d.borough || "";

		return (
			<span>
				<span className="building_name">
					{_capitalizeText(buildingNameOrAdr || adr)}
				</span>
				{buildingNameOrAdr !== adr && adr && (
					<>
						<br />
						<span className="light-text small-text">
							{_capitalizeText(adr)}
						</span>
					</>
				)}
				<br />
				<span className="light-text small-text">
					{hood ? `${_capitalizeText(hood)}, ` : ""}
					{_capitalizeText(boro)}
				</span>
			</span>
		);
	},
	property_category: (d) =>
		_capitalizeText((d.property_category || "").replace(/-/g, " ")),
	ownership_type: (d) =>
		_capitalizeText((d.ownership_type || "").replace(/-/g, " ")),
	borough: (d) => _capitalizeText(d.borough || ""),
	neighborhood: (d) => {
		const hood = Array.isArray(d.neighborhood)
			? d.neighborhood[0]
			: d.neighborhood;
		return _capitalizeText(hood || "");
	},
	year_built: (d) => (d.year_built > 0 ? d.year_built : "--"),
	total_floors: (d) =>
		d.total_floors > 0 ? _formatNumber(d.total_floors) : "--",
	total_units: (d) =>
		d.total_units > 0 ? _formatNumber(d.total_units) : "--",
	residential_units: (d) =>
		d.residential_units > 0 ? _formatNumber(d.residential_units) : "--",
	inventory_total: (d) =>
		d.inventory_total > 0 ? _formatNumber(d.inventory_total) : "--",
	inventory_unsold: (d) =>
		d.inventory_unsold >= 0 ? _formatNumber(d.inventory_unsold) : "--",
	inventory_active: (d) =>
		d.inventory_active >= 0 ? _formatNumber(d.inventory_active) : "--",
	building_effective_start: (d) =>
		d.building_effective_start
			? moment(d.building_effective_start).format("MM/DD/YYYY")
			: "--",
	sales_start_date: (d) =>
		d.sales_start_date
			? moment(d.sales_start_date).format("MM/DD/YYYY")
			: "--",
	offering_submitted_date: (d) =>
		d.offering_submitted_date
			? moment(d.offering_submitted_date).format("MM/DD/YYYY")
			: "--",
	offering_accepted_date: (d) =>
		d.offering_accepted_date
			? moment(d.offering_accepted_date).format("MM/DD/YYYY")
			: "--",
	marketing_company: (d) => formatArrayField(d.marketing_company),
	developers: (d) => formatArrayField(d.developers),
	architect: (d) => formatArrayField(d.architect),
	owner: (d) => formatArrayField(d.owner),
	lenders: (d) => formatArrayField(d.lenders),
	building_status: (d) => _capitalizeText(d.building_status || ""),
	building_category: (d) =>
		_capitalizeText((d.building_category || "").replace(/-/g, " ")),
	type: (d) => _capitalizeText(d.type || ""),
	zip: (d) => d.zip || "--",
	solr_updated: (d) =>
		d.solr_updated
			? moment(d.solr_updated).format("MM/DD/YYYY")
			: "--",
};

const renderCell = (d, colId) => {
	if (cellRenderers[colId]) {
		return cellRenderers[colId](d);
	}
	const val = d[colId];
	if (val === undefined || val === null || val === "") return "--";
	return String(val);
};

class MatTableView extends PureComponent {
	render() {
		const {
			order,
			orderBy,
			selected,
			reactTableFiltered,
			tableData,
			totalCount,
			rowsPerPage,
			page,
			handleBuildingClick,
			handleChangePage,
			handleChangeRowsPerPage,
			handleRequestSort,
			onSelectAllClick,
			visibleColumns,
		} = this.props;

		const table = reactTableFiltered;
		const paginationCount = totalCount;

		const activeColumns = buildingSearchColumns.filter(
			(col) => col.alwaysVisible || visibleColumns.includes(col.id)
		);

		return (
			<Fragment>
				<div className="material-table__wrap">
					<Table className="material-table">
						<MatTableHead
							numSelected={
								selected ? [...selected].filter((el) => el[1]).length : 0
							}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={onSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={tableData ? tableData.length : 0}
							visibleColumns={visibleColumns}
						/>
						<TableBody>
							{table &&
								table.map((d) => {
									const buildingURL = _getBuildingURL(d, "key");
									return (
										<TableRow
											className="material-table__row"
											role="checkbox"
											onClick={(event) =>
												handleBuildingClick(event, d)
											}
											tabIndex={-1}
											key={d.key || d.id}
											style={{ cursor: "pointer" }}
										>
											<TableCell
												className="material-table__cell"
												padding="checkbox"
												onClick={(e) => e.stopPropagation()}
											>
												<Checkbox
													className="material-table__checkbox"
													checked={false}
												/>
											</TableCell>
											{activeColumns.map((col) => (
												<TableCell
													key={col.id}
													className="material-table__cell material-table__cell-right"
												>
													{col.id === "display_full_street_address" ? (
														<Link
															to={buildingURL}
															onClick={(e) => e.stopPropagation()}
														>
															{renderCell(d, col.id)}
														</Link>
													) : (
														renderCell(d, col.id)
													)}
												</TableCell>
											))}
										</TableRow>
									);
								})}
						</TableBody>
					</Table>
				</div>
				<TablePagination
					component="div"
					className="material-table__pagination"
					count={paginationCount}
					rowsPerPage={rowsPerPage}
					page={page}
					backIconButtonProps={{ "aria-label": "Previous Page" }}
					nextIconButtonProps={{ "aria-label": "Next Page" }}
					onChangePage={handleChangePage}
					onChangeRowsPerPage={handleChangeRowsPerPage}
					rowsPerPageOptions={[25, 40, 100]}
					dir="ltr"
					SelectProps={{
						inputProps: { "aria-label": "rows per page" },
						native: true,
					}}
				/>
			</Fragment>
		);
	}
}

export default connect((state) => ({
	user: state.user,
}))(MatTableView);
